import { dasherizeParts, FlagState, Stat } from "@timing71/common";
import { RaceControlMessage } from '@timing71/common/messages';

const CATEGORIES = {
  2: 'LMGTEPro',
  3: 'LMP2',
  4: 'LMGTEAm',
  6: 'LMP3',
  10: 'GTE',
  16: 'GT3',
  18: 'INV',
  4166: 'LMP2 P/A',
  4167: 'Hypercar',
  4172: "LMP2 P/A",
  4179: 'LMGT3',
  4183: 'LMGT3'
};

const CAR_STATES = {
  'Run': 'RUN',
  'In': 'PIT',
  'Out': 'OUT',
  'Stop': 'STOP',
  'Ret': 'RET',
  'Chk': 'FIN'
};

const FLAGS = {
  'green': FlagState.GREEN,
  'red': FlagState.RED,
  'yellow': FlagState.YELLOW,
  'full_yellow': FlagState.FCY,
  'safety_car': FlagState.SC,
  'virtual_safety_car': FlagState.VSC,
  'chk': FlagState.CHEQUERED,
  'off': FlagState.NONE
};

const DRIVER_RANKS = {
  'B': 'bronze',
  'S': 'silver',
  'G': 'gold',
  'P': 'platinum'
};

const arrayToMap = (arr, keyFunc=(_, i) => i) => {
  return Object.fromEntries(
    arr.filter(a => !!a).map((a, i) => [keyFunc(a, i), a])
  );
}

const noOp = () => {};

export class Client {

  constructor(name, onUpdate, updateManifest, onSessionChange, fetchFunc, reference, postprocessCar=noOp) {
    this.entries = {};
    this.params = {};
    this.race_control = [];
    this.stints = {};
    this.name = name;
    this.onUpdate = () => onUpdate(this);
    this.updateManifest = updateManifest;
    this.onSessionChange = onSessionChange;
    this.handle = this.handle.bind(this);
    this.getManifest = this.getManifest.bind(this);
    this.getState = this.getState.bind(this);
    this.mapCar = this.mapCar.bind(this);
    this._load_ref_data = this._load_ref_data.bind(this);
    this.postProcessCar = postprocessCar.bind(this);

    this.fetchFunc = fetchFunc;
    this.reference = reference;

    this._load_ref_data();

    updateManifest(this.getManifest());
  }

  _load_ref_data(force) {
    this.reference.load(this.fetchFunc, force).then(
      (d) => this.handle('reference_data', d)
    ).catch(
      () => {
        setTimeout(this._load_ref_data, 30000);
      }
    );
  }

  handle(event, data) {
    switch(event) {

      case 'race':
        this.handle('params', data.params);
        this.handle('entries', data.entries);
        break;

      case 'entries':
        data.forEach(
          entry => {
            if (entry) {
              this.entries[entry.id] = entry;
            }
          }
        );
        break;

      case 'params':
        if (!!data.sessionId) {
          if (this.params.sessionId && this.params.sessionId !== data.sessionId) {
            this.onSessionChange();
          }
          this.params = {
            ...this.params,
            ...data
          };
          this.updateManifest(this.getManifest());
        }
        break;

      case 'race_control':
        this.race_control = [...data];
        break;

      case 'clear_entries':
        this.entries = {};
        break;

      case 'refresh':
        this._load_ref_data(true);
        break;

      case 'stints':
        const newStints = arrayToMap(data, i => i.participant);
        this.stints = {
          ...this.stints,
          ...newStints
        };
        break;

      case 'laps':
      case 'flags':
      case 'race_light':
      case 'best_sectors':
        // Ignore these (for now)
        break;

      case 'reference_data':
        this.updateManifest(this.getManifest());
        break;

      default:
        console.warn(`Unhandled event: ${event}`, data); // eslint-disable-line no-console

    }

    this.onUpdate();
  }

  getManifest() {
    // Avoid repetition of LM24 branding in name and description
    const raceName = this.name === 'Le Mans 24 Hours' ? null : this.reference.data.race?.name_en;

    const description = dasherizeParts(
      raceName,
      this.params.sessionName || 'No current session'
    );

    return {
      name: this.name,
      description,
      colSpec: [
        Stat.NUM,
        Stat.STATE,
        Stat.CLASS,
        Stat.POS_IN_CLASS,
        Stat.TEAM,
        Stat.DRIVER,
        Stat.CAR,
        Stat.LAPS,
        Stat.GAP, // 8
        Stat.INT, // 9
        Stat.S1,
        Stat.BS1,
        Stat.S2,
        Stat.BS2,
        Stat.S3,
        Stat.BS3,
        Stat.LAST_LAP,
        Stat.BEST_LAP,
        Stat.SPEED,
        Stat.PITS
      ],
      trackDataSpec: [
        'Track temp',
        'Air temp',
        'Humidity',
        'Pressure',
        'Wind speed',
        'Wind direction'
      ]
    };
  }

  /*
    Upstream expects the state returned from this call to include only _new_ messages
    in the `extraMessages` array. Feels wrong that two consecutive calls to `getState`
    would return different results, so it's up to the caller to remember the latest
    race control message they received and tell us where to start looking.
  */
  getState(prevRaceControlTimestamp=0, parameters={}) {
    const sortedEntries = Object.values(this.entries).sort((a, b) => (a.ranking || 999) - (b.ranking || 999));
    const newRCMessages = this.race_control.filter(m => m.dayTime > prevRaceControlTimestamp).reverse();

    const extraMessages = newRCMessages.map(
      msg => new RaceControlMessage(msg.text, msg.dayTime).toCTDFormat()
    );

    const latestRCTimestamp = Math.max(...this.race_control.map(m => m.dayTime));

    return {
      cars: this.postprocessCars(sortedEntries.map(this.mapCar), this.stints, parameters),
      extraMessages,
      meta: {
        raceControlTimestamp: latestRCTimestamp
      },
      session: {
        timeElapsed: this.params.elapsedTime,
        timeRemain: Math.ceil(Math.max(this.params.remaining, 0)),
        flagState: FLAGS[this.params.raceState?.toLowerCase()] || FlagState.NONE,
        pauseClocks: this.params.stoppedSinceTime > 0,
        trackData: [
          [Math.round((this.params.trackTemp || 0) * 10) / 10, '°C'],
          [Math.round((this.params.airTemp || 0) * 10) / 10, '°C'],
          [Math.round(this.params.humidity || 0), '%'],
          [Math.round((this.params.pressure || 0) * 10) / 10, 'mbar'],
          [Math.round((this.params.windSpeed || 0) * 10) / 10, 'kph'],
          [Math.round(this.params.windDirection || 0), '°']
        ]
      }
    };
  }

  mapCar(car) {

    const llFlag = (car.lastlapTime > 0 && car.lastlapTime === car.bestlapTime) ? 'pb' : '';

    let driver = car.driver;
    let driverRank = '';

    if (car.driverId && car.driverId <= car.drivers.length) {
      const drv = car.drivers[car.driverId - 1];
      if (drv) {
        driver = `${drv.lastName}, ${drv.firstName}`;
        driverRank = DRIVER_RANKS[drv.license] || 'unknown';
      }
    }
    else if (car.drivers) {
      const maybeDriver = car.drivers.find(d => `${d.lastName} ${d.firstName}` === car.driver);
      if (maybeDriver) {
        driver = `${maybeDriver.lastName}, ${maybeDriver.firstName}`
      }
    }

    const carId = car.id;
    const entry = (this.reference.data.entries || []).find(c => c.id === carId);

    const category = entry?.category_label?.replace('LM ', 'LM')?.replace('Hypercar H', 'Hypercar') || CATEGORIES[car.categoryId] || car.category || String(car.categoryId);

    return ([
      car.number,
      CAR_STATES[car.state] || car.state,
      category,
      car.categoryRanking,
      car.team,
      [driver, driverRank],
      car.car,
      car.lap,
      car.gapTime ? car.gapTime / 1000 : car.gap !== "0.000" && car.gap !== '-' ? car.gap : '',
      car.gapPrevTime ? car.gapPrevTime / 1000 : car.gapPrev !== "0.000" && car.gapPrev !== '-' ? car.gapPrev : '',
      [car.currentSector1 !== null ? car.currentSector1 === 'NaN' ? '-' : car.currentSector1 : '', car.currentSector1 === car.bestSector1 ? 'pb' : ''],
      [car.bestTimeSector1 > 0 ? car.bestTimeSector1 / 1000 : '', 'old'],
      [car.currentSector2 !== null ? car.currentSector2 === 'NaN' ? '-' : car.currentSector2 : '', car.currentSector2 === car.bestSector2 ? 'pb' : ''],
      [car.bestTimeSector2 > 0 ? car.bestTimeSector2 / 1000 : '', 'old'],
      [car.currentSector3 !== null ? car.currentSector3 === 'NaN' ? '-' : car.currentSector3 : '', car.currentSector3 === car.bestSector3 ? 'pb' : ''],
      [car.bestTimeSector3 > 0 ? car.bestTimeSector3 / 1000 : '', 'old'],
      [car.lastlapTime > 0 ? car.lastlapTime / 1000 : '', llFlag],
      [car.bestlapTime > 0 ? car.bestlapTime / 1000 : '', ''],
      car.speed !== '-' ? car.speed : '',
      [car.pitstop, '']
    ]);
  }

  postprocessCars(cars, stints, parameters) {

    if (cars.length >= 1) {
      // Fix data showing gap and int for leading car!
      cars[0][8] = '';
      cars[0][9] = '';

      const bestLapsByClass = {};
      const classIdx = 2;
      const lastLapIdx = 16;
      const bestLapIdx = 17;

      const bestSectorsByClass = {};

      cars.forEach(
        (car, idx) => {
          const clazz = car[classIdx];
          const blt = car[bestLapIdx][0];

          if (blt > 0 && (!bestLapsByClass[clazz] || bestLapsByClass[clazz][1] > blt)) {
            bestLapsByClass[clazz] = [idx, blt];
          }

          if (!bestSectorsByClass[clazz]) {
            bestSectorsByClass[clazz] = {};
          }

          [1, 2, 3].forEach(
            sector => {
              const st = car[9 + (2 * sector)][0];
              if (st > 0 && (!bestSectorsByClass[clazz][sector] || bestSectorsByClass[clazz][sector][1] > st)) {
                bestSectorsByClass[clazz][sector] = [idx, st];
              }
            }
          );

          this.postProcessCar(car, stints, parameters);

        }
      );

      Object.values(bestLapsByClass).forEach(
        ([idx, blt]) => {
          const llt = cars[idx][lastLapIdx][0];
          cars[idx][bestLapIdx] = [blt, 'sb'];

          const s3 = cars[idx][14];
          if (llt === blt && !!s3[0]) {
            cars[idx][lastLapIdx] = [llt, 'sb-new'];
          }
        }
      );

      Object.values(bestSectorsByClass).forEach(
        bestSectors => {
          [1, 2, 3].forEach(
            sector => {
              if (bestSectors[sector]) {
                const sectorIdx = 9 + (2 * sector);
                const [idx, st] = bestSectors[sector];
                cars[idx][sectorIdx] = [st, 'sb'];

                if (cars[idx][sectorIdx - 1][1] === 'pb') {
                  // Unfortunately current sector time is a string, so we can't
                  // compare it without parsing it. But if the time was a personal
                  // best we know we're correct in doing this:
                  cars[idx][sectorIdx - 1] = [st, 'sb'];
                }
              }
            }
          );
        }
      );
    }

    return cars;
  };

}
